import {
    Button,
    Center,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Spinner,
    Text,
    useDisclosure,
    useToast,
} from '@chakra-ui/react';
import {
    LinkAuthenticationElement,
    PaymentElement,
    useElements,
    useStripe,
} from '@stripe/react-stripe-js';
import { Layout, StripeError } from '@stripe/stripe-js';
import { useEffect, useState } from 'react';
import { FormattedNumber } from 'react-intl';
import { AuthenticatedComponent, AuthenticatedKeycloak } from '../../auth/keycloak';
import { ProductDef } from '../../service/inventory';

interface CheckoutFormProps {
    clientSecret: string;
    product: ProductDef | undefined;
    onFormClose(err: StripeError | undefined): void;
    keycloak: AuthenticatedKeycloak;
    email: string;
}

export const CheckoutForm: AuthenticatedComponent<CheckoutFormProps> = ({
    clientSecret,
    product,
    onFormClose,
    keycloak,
    email,
}) => {
    const stripe = useStripe();
    const elements = useElements();

    const [message, setMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const { isOpen, onOpen, onClose } = useDisclosure();
    const [paymentError, setPaymentError] = useState<StripeError | undefined>();
    const [state, setState] = useState('payment');
    const [resultMessage, setResultMessage] = useState<string>('');

    const toast = useToast();

    useEffect(() => {
        if (!stripe) {
            return;
        }

        if (!clientSecret) {
            return;
        }

        stripe.retrievePaymentIntent(clientSecret).then(({ paymentIntent }) => {
            switch (paymentIntent?.status) {
                case 'succeeded':
                    setMessage('Payment succeeded!');
                    setResultMessage('Payment Successful');
                    setState('result');
                    onOpen();
                    break;
                case 'processing':
                    setMessage('Your payment is processing.');
                    setResultMessage('Your payment is in process');
                    break;
                case 'requires_payment_method':
                    setMessage('');
                    setResultMessage('');
                    break;
                default:
                    setMessage('Something went wrong.');
                    setResultMessage('Payment Error');
                    break;
            }
        });
    }, [stripe]);

    const handleSubmit = async () => {
        if (!stripe || !elements) {
            // Stripe.js hasn't yet loaded.
            // Make sure to disable form submission until Stripe.js has loaded.
            return;
        }

        setIsLoading(true);

        const { error } = await stripe.confirmPayment({
            elements,
            confirmParams: {
                return_url: `${window.location.origin}/store`,
            },
            redirect: 'if_required',
        });

        setIsLoading(false);
        setPaymentError(error);

        // For some payment methods like iDEAL, your customer will
        // be redirected to an intermediate site first to authorize the payment, then
        // redirected to the `return_url`.
        if (error) {
            if (error.type === 'card_error' || error.type === 'validation_error') {
                setMessage(error.message || 'error without message');
                setResultMessage('Payment Failed');
            } else {
                setMessage('An unexpected error occurred.');
                setResultMessage('Payment Failed');
            }
        } else {
            setResultMessage('Payment Successful');
            console.log(`[CheckoutForm] stripe payment completed`);
        }
        setState('result');
        onOpen();
    };

    const authElementOptions = {
        defaultValues: {
            email: email,
        },
    };

    const paymentElementOptions: { layout: Layout } = {
        layout: 'tabs',
    };

    const renderPayment = () => (
        <>
            <ModalHeader className={'purchase-modal-header cost-header'}>
                <Text>
                    {`${product?.product_name} for `}
                    <FormattedNumber
                        value={product?.cost[0].quantity || 0}
                        style={'currency'}
                        currency={product?.cost[0].currency}
                    />
                </Text>
            </ModalHeader>
            <ModalBody>
                <LinkAuthenticationElement
                    id="link-authentication-element"
                    options={authElementOptions}
                />
                <PaymentElement
                    id="payment-element"
                    options={paymentElementOptions}
                    onLoaderStart={() => {
                        toast({ status: 'info', description: 'started!' });
                    }}
                />
                {message}
            </ModalBody>
            <ModalFooter className="purchase-modal-footer">
                <Button
                    isDisabled={isLoading || !stripe || !elements}
                    onClick={() => {
                        handleSubmit();
                    }}
                >
                    {isLoading ? <Spinner data-cy="loading" /> : 'Pay now'}
                </Button>
                <Button
                    onClick={() => {
                        onFormClose(undefined);
                    }}
                >
                    Cancel
                </Button>
            </ModalFooter>
        </>
    );

    const renderResult = () => (
        <div className={'result-content'}>
            <ModalHeader className={'result-modal-header'}>
                <Center>
                    <Text>{resultMessage}</Text>
                </Center>
            </ModalHeader>
            <ModalBody className={'result-modal-body'}></ModalBody>
            <ModalFooter className={'result-modal-footer'}>
                <Button
                    onClick={() => {
                        onFormClose(undefined);
                    }}
                >
                    Close
                </Button>
            </ModalFooter>
        </div>
    );

    return <>{state === 'payment' ? renderPayment() : renderResult()}</>;
};
